/**
 * `@docesdamaria`, from whatever she typed into the onboarding field.
 *
 * The field takes free text — its placeholder shows a full address, and people
 * paste profile URLs, bare handles and `@handle` interchangeably — so every
 * surface that draws it normalises rather than trusting the shape. A trailing
 * slash from a copied URL and a query string from a share link both end up
 * dropped.
 *
 * It lives beside the types rather than inside one screen because two draw it:
 * the shell's footer and the generator's profile card. The first copy of this
 * to drift is the one that prints `@instagram.com` beside her name.
 *
 * Undefined when nothing usable is left, which is what hides the row rather
 * than drawing a bare `@`.
 */
export function instagramHandle(instagram: string | undefined): string | undefined {
  if (!instagram) {
    return undefined;
  }
  const handle = instagram
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    // Trailing slash optional: `https://instagram.com` with nothing after it
    // otherwise survived the strip and got printed back as `@instagram.com`.
    .replace(/^instagram\.com\/?/i, '')
    .split(/[/?#]/)[0]!
    .replace(/^@/, '');
  return handle === '' ? undefined : `@${handle}`;
}

const ALLOWED = /^[A-Za-z0-9._]{1,30}$/;

/** Instagram's own username rules. Says nothing about whether the account exists. */
export function isInstagramHandle(typed: string | undefined): boolean {
  const handle = instagramHandle(typed)?.slice(1);
  if (handle === undefined || !ALLOWED.test(handle)) {
    return false;
  }
  return !handle.startsWith('.') && !handle.endsWith('.') && !handle.includes('..');
}

export type Network = 'instagram' | 'tiktok' | 'youtube';

/**
 * The same shapes she pastes, for the three networks. The server normalises
 * again and is the authority — this exists so the button can say what is wrong
 * before a round trip, not so the server can trust it.
 */
const HOSTS: Record<Network, RegExp> = {
  instagram: /^(www\.)?instagram\.com$/i,
  tiktok: /^((www|vm)\.)?tiktok\.com$/i,
  youtube: /^((www|m)\.)?youtube\.com$/i
};

const RULES: Record<Network, RegExp> = {
  instagram: /^[A-Za-z0-9._]{1,30}$/,
  tiktok: /^[A-Za-z0-9._]{2,24}$/,
  youtube: /^(@?[A-Za-z0-9._-]{3,30}|UC[A-Za-z0-9_-]{22})$/
};

/** Undefined when nothing usable is left, which is what keeps the field empty. */
export function networkHandle(network: Network, typed: string | undefined): string | undefined {
  if (!typed) {
    return undefined;
  }
  const trimmed = typed.trim().replace(/^https?:\/\//i, '');
  const [host, ...path] = trimmed.split('/');
  const fromUrl = HOSTS[network].test(host ?? '');
  if (trimmed.includes('/') && !fromUrl) {
    return undefined;
  }

  const segments = path.filter(Boolean);
  // `/@nome`, `/channel/UC…`, `/c/nome` and `/user/nome` all end in the part
  // that names the channel; the other two networks put it first.
  const raw = fromUrl
    ? (network === 'youtube' ? segments.at(-1) : segments[0])
    : (trimmed.split(/[?#]/)[0] ?? '');

  const candidate = (raw ?? '').split(/[?#]/)[0]!.replace(/^@/, '');
  if (candidate === '') {
    return undefined;
  }
  // A channel id is case-sensitive; a handle is not, and folding it keeps one
  // key per account.
  return network === 'youtube' && candidate.startsWith('UC') ? candidate : candidate.toLowerCase();
}

/** Each network's own username rules. Says nothing about whether it exists. */
export function isNetworkHandle(network: Network, typed: string | undefined): boolean {
  const handle = networkHandle(network, typed);
  if (handle === undefined || !RULES[network].test(handle)) {
    return false;
  }
  if (network !== 'instagram') {
    return true;
  }
  return !handle.startsWith('.') && !handle.endsWith('.') && !handle.includes('..');
}
